import { useMutation } from '@apollo/react-hooks';
import { InfoOutlined } from '@material-ui/icons';
import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { RadioGroup } from '../../../../components';
import { useAuth } from '../../../../context';
import {
  ClaimStoreProgramParticipant,
  ClaimStoreProgramParticipantVariables,
} from '../../../../generated/ClaimStoreProgramParticipant';
import { StoreProgramParticipantAdvance } from '../../../../generated/StoreProgramParticipantAdvance';
import {
  UpdateProgramParticipant,
  UpdateProgramParticipantVariables,
} from '../../../../generated/UpdateProgramParticipant';
import {
  AttachmentCardTypes,
  CategoryDetailEnum,
  ClaimStoreType,
  PreferenceChecklistInput,
  ProgramUnverifiedReasonDataInput,
  StoreProgramStatus,
} from '../../../../generated/globalTypes';
import {
  CLAIM_STORE_PROGRAM_PARTICIPANT,
  UPDATE_PROGRAM_PARTICIPANT,
} from '../../../../graphql/mutations/programParticipant';
import { Dropdown, SegmentedControl } from '../../components';
import {
  categoryDetailUnverifPP,
  categoryTypeUnverifPP,
} from '../../constants/categoryUnverifPP';
import { colors, spacing, theme } from '../../constants/theme';
import { useSnackbar } from '../../context';
import { Button, Switch, Text, TextInput } from '../../core-ui';

import {
  ChecklistAnswer,
  findAnswer,
  getSubmittedAnswer,
  isChecklistComplete,
} from './ProgramQuestionaireForm';
import { TourParticipantBox } from './TourParticipantBox';
import { UploadFieldInput } from './UploadFileInput';

type ParticipantFormData = StoreProgramParticipantAdvance['storeProgramParticipantAdvance']['row'][0];
type AttachmentFormData = Omit<
  NonNullable<ParticipantFormData['attachments']>,
  '__typename'
>;

type Props = {
  data: ParticipantFormData;
  refetchData: () => void;
  onFormHasChange?: (changed: boolean) => void;
  viewMode?: boolean;
  adminEditMode?: boolean;
  onEditableChange?: (editable: boolean) => void;
  // NOTE: answers of each participant checklist, one array per participant
  checklistAnswers?: Array<Array<ChecklistAnswer>>;
};

type ProgramUnverifiedReasonDataInputState = Omit<
  ProgramUnverifiedReasonDataInput,
  'categoryDetailEnum'
> & { categoryDetailEnum: CategoryDetailEnum | null };

let emptyUnverifiedCategories: ProgramUnverifiedReasonDataInputState = {
  categoryDetail: '',
  categoryType: '',
  nextAction: '',
  problemDetail: '',
  categoryDetailEnum: null,
};

export function ProgramParticipantForm({
  viewMode,
  data,
  refetchData,
  onFormHasChange,
  adminEditMode,
  onEditableChange,
  checklistAnswers,
}: Props) {
  let [attachmentForm, setAttachmentForm] = useState<AttachmentFormData | null>(
    data.attachments,
  );
  let [formChanged, setFormChanged] = useState(false);

  let [selectedRadio, setSelectedRadio] = useState(
    data.attachments?.ownerNPWPType,
  );
  let [unverifiedCategories, setUnverifiedCategories] = useState<
    Array<ProgramUnverifiedReasonDataInputState> | undefined
  >([emptyUnverifiedCategories]);

  let [editable, setEditable] = useState(false);

  type VerifStatus = Exclude<StoreProgramStatus, StoreProgramStatus.WAITING>;

  let verifStatusOptions: Array<VerifStatus> = Object.values(
    StoreProgramStatus,
  ).filter((status) => status !== StoreProgramStatus.WAITING) as Array<
    VerifStatus
  >;

  const [currentStatus, setCurrentStatus] = useState<StoreProgramStatus>(
    viewMode
      ? data.status
      : adminEditMode
      ? StoreProgramStatus.VERIFIED
      : StoreProgramStatus.UNVERIFIED,
  );

  const { openSnackbar } = useSnackbar();

  const { id } = useAuth();

  function undefinedToNull<T extends object>(obj: Partial<T>): T {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === undefined ? null : v]),
    ) as T;
  }

  let onChangeForm = (data: Partial<AttachmentFormData>) => {
    setFormChanged(true);
    onFormHasChange && onFormHasChange(true);
    setAttachmentForm((prev) => {
      const updatedForm = {
        ...prev,
        ...undefinedToNull<AttachmentFormData>(data),
      };
      return updatedForm;
    });
  };

  let [unClaimProgramParticipant, { loading: unclaimLoading }] = useMutation<
    ClaimStoreProgramParticipant,
    ClaimStoreProgramParticipantVariables
  >(CLAIM_STORE_PROGRAM_PARTICIPANT, {
    onCompleted: () => {
      refetchData();
      openSnackbar({
        variant: 'success',
        message: t([
          'Klaim permohonan verifikasi telah dibatalkan.',
          'Verification request has been unclaimed.',
        ]),
      });
    },
    onError: () => {
      openSnackbar({
        variant: 'error',
        message: t([
          'Gagal membatalkan klaim permohonan verifikasi. Mohon coba lagi nanti.',
          'Failed to unclaim verification request. Please try again later.',
        ]),
      });
    },
  });

  let [updateProgramParticipant, { loading: updatedLoading }] = useMutation<
    UpdateProgramParticipant,
    UpdateProgramParticipantVariables
  >(UPDATE_PROGRAM_PARTICIPANT, {
    onCompleted: () => {
      openSnackbar({
        variant: 'success',
        message: t(['Data telah diperbarui.', 'Data has been updated.']),
      });
      refetchData();
    },
    onError: () => {
      openSnackbar({
        variant: 'error',
        message: t([
          'Gagal memperbarui data. Mohon coba lagi nanti.',
          'Failed to update data. Please try again later.',
        ]),
      });
    },
  });

  let onUpdatePress = () => {
    //Get Object to test for value
    let testAttachment =
      attachmentForm &&
      Object.fromEntries(
        Object.entries(attachmentForm).filter(([key]) => {
          // Filter out is Owner data from test data
          // Since we don't do anything to the value in this scene
          if (!participant2Exist) {
            // Filter out secondParticipant data from test if no second participant available
            return !key.includes('secondParticipant') && !key.startsWith('is');
          }
          return !key.startsWith('is');
        }),
      );

    //Check if base form has been filled
    let baseFormReady =
      currentStatus !== StoreProgramStatus.WAITING &&
      testAttachment &&
      Object.values(testAttachment).every((v) => Boolean(v)) &&
      Boolean(selectedRadio);

    //Check if Unverfied categories are filled
    let unverifiedReady = unverifiedCategories?.every((category) =>
      Object.values(category).every((v) => Boolean(v)),
    );

    let preferenceData = getPreferenceData();

    //Check if the questionaire is filled, only when it's being edited
    let questionaireReady =
      !editable ||
      !checklistAnswers ||
      preferenceData.every(({ checklistData }) =>
        isChecklistComplete(data.checklistQuestions ?? [], checklistData),
      );

    let isUnverified = currentStatus === StoreProgramStatus.UNVERIFIED;

    let shouldProceed =
      questionaireReady &&
      (isUnverified ? baseFormReady && unverifiedReady : baseFormReady);

    if (shouldProceed) {
      if (data.store && data.StoreProgram) {
        let { __typename, ...otherAttachment } = attachmentForm as NonNullable<
          ParticipantFormData['attachments']
        >;
        updateProgramParticipant({
          variables: {
            storeId: data.store.id,
            programId: data.StoreProgram.id,
            status: currentStatus,
            unverifiedReasonData:
              isUnverified && unverifiedCategories
                ? (unverifiedCategories.filter(
                    (v) => v.categoryDetailEnum != null,
                  ) as Array<ProgramUnverifiedReasonDataInput>)
                : undefined,
            attachment: {
              ...otherAttachment,
              ownerNPWPType: selectedRadio,
            },
            // NOTE: remove the "Lainnya" option value from multi choice answers
            preferenceData: preferenceData.map(
              ({ phoneNumber, checklistData }) => ({
                phoneNumber,
                checklistData: checklistData.map((item) => {
                  let question = data.checklistQuestions?.find(
                    ({ order }) => order === item.order,
                  );
                  return question
                    ? {
                        ...item,
                        answer: getSubmittedAnswer(question, item.answer),
                      }
                    : item;
                }),
              }),
            ),
          },
        });
      }
    } else {
      openSnackbar({
        message: t([
          'Pastikan semua field dengan simbol * sudah terisi',
          'Make sure every field with * has been filled',
        ]),
        variant: 'error',
      });
    }
  };

  // NOTE: send every question for each participant, unanswered questions are sent
  // with a blank answer so the app shows an empty form
  let getPreferenceData = (): Array<PreferenceChecklistInput> => {
    let sortedQuestions = [...(data.checklistQuestions ?? [])].sort(
      (a, b) => a.order - b.order,
    );
    let phoneNumbers = [
      attachmentForm?.firstParticipantPhoneNumber,
      attachmentForm?.secondParticipantPhoneNumber,
    ].filter((phoneNumber): phoneNumber is string => !!phoneNumber);

    return phoneNumbers.map((phoneNumber, index) => {
      // NOTE: fallback to the saved answers when the questionaire isn't passed
      let answers =
        checklistAnswers?.[index] ??
        data.preferenceCheckList?.find((p) => p.phoneNumber === phoneNumber)
          ?.checklistData ??
        [];
      return {
        phoneNumber,
        checklistData: sortedQuestions.map((question) => ({
          question: question.value,
          order: question.order,
          answer: findAnswer(question, answers),
        })),
      };
    });
  };

  let isButtonDisabled = () => {
    if (adminEditMode) {
      return !(formChanged && currentStatus === StoreProgramStatus.VERIFIED);
    }
    return false;
  };

  let onChangeUnverifiedCategories = (
    data: Partial<ProgramUnverifiedReasonDataInput>,
    index: number,
    reset?: Array<keyof ProgramUnverifiedReasonDataInput>,
  ) => {
    onFormHasChange && onFormHasChange(true);
    setUnverifiedCategories((prev) => {
      if (!prev) {
        return prev;
      }

      function setByKey<T, K extends keyof T>(target: T, source: T, key: K) {
        // This function are used so TS won't complain about modifying
        // unknown object prop to another unknown value
        //
        // e.g updated[key] = emptyUnverifiedCategories[key];
        // will trigger TS Error even tho it has the same structure and are being accessed by the same key
        // This is because TS can't guarantee that both target and source are the same type
        // or are being assigned the same value type
        target[key] = source[key];
      }

      return prev.map((unverifiedCategory, idx) => {
        if (idx !== index) {
          return unverifiedCategory;
        }

        const updated = {
          ...unverifiedCategory,
          ...data,
        };

        // Optional reset logic
        if (reset?.length) {
          for (const key of reset) {
            setByKey(updated, emptyUnverifiedCategories, key);
          }
        }

        return updated;
      });
    });
  };

  let deleteUnverifiedCategories = (index: number) => {
    const newUnverifCategories = unverifiedCategories?.filter(
      (_, idx) => idx !== index,
    );
    setUnverifiedCategories(newUnverifCategories);
  };

  let owner = data.store?.User;

  let {
    firstParticipantFullName,
    firstParticipantIDPhoto,
    firstParticipantPassport,
    firstParticipantPhoneNumber,
    firstParticipantPortraitPhoto,
    ownerKK,
    ownerNPWP,
    secondParticipantFullName,
    secondParticipantIDPhoto,
    secondParticipantPassport,
    secondParticipantPhoneNumber,
    secondParticipantPortraitPhoto,
    isFirstParticipantOwner,
    isSecondParticipantOwner,
  } = attachmentForm || {};

  let participant2Exist =
    secondParticipantPhoneNumber || secondParticipantFullName;

  let resetData = () => {
    setAttachmentForm(data.attachments);
    setSelectedRadio(data.attachments?.ownerNPWPType);
  };

  return (
    <View style={styles.rootContainer}>
      <View style={styles.title}>
        <Text bold size="s" color="white">
          {t([
            'Verifikasi Toko Program Principal',
            'Program Principal Store Verification',
          ])}
        </Text>
        <View style={styles.editModeContainer}>
          <Text color="white" style={{ marginRight: 8 }}>
            Edit Mode
          </Text>
          <Switch
            checked={editable}
            onPress={(check) => {
              setEditable(check);
              onEditableChange?.(check);
              setCurrentStatus(StoreProgramStatus.VERIFIED);
              resetData();
            }}
            disabled={viewMode || data.admin?.id !== id}
          />
        </View>
      </View>
      <View style={styles.formContainer}>
        <View>
          <Text bold size="m" style={styles.viewMode}>
            {t(['Data Toko', 'Store Data'])}
          </Text>
          <TextInput
            containerStyle={styles.marignTop16}
            data-testid="store-name"
            value={data.store?.storeName}
            onChangeText={() => {}}
            disabled
            label={t(['Nama toko', 'Store Name'])}
          />
          <TextInput
            containerStyle={styles.marignTop16}
            data-testid="store-code"
            value={data.store?.managerStoreCode}
            onChangeText={() => {}}
            disabled
            label={t(['Kode toko', 'Store Code'])}
          />
          <TextInput
            containerStyle={styles.marignTop16}
            data-testid="owner-name"
            value={owner?.profile?.name}
            onChangeText={() => {}}
            disabled
            label={t(['Nama Pemilik Toko', 'Store Owner Name'])}
          />
          <TextInput
            containerStyle={styles.marignTop16}
            data-testid="owner-phone-number"
            value={owner?.phoneNumber}
            onChangeText={() => {}}
            disabled
            label={t(['Nomor HP Pemilik Toko', 'Store Owner Phone Number'])}
          />
          <TextInput
            containerStyle={styles.marignTop16}
            data-testid="store-city"
            value={data.store.cityName}
            onChangeText={() => {}}
            disabled
            label="Kota/Kabupaten"
          />
        </View>
        <View style={styles.infoContainer}>
          <InfoOutlined
            style={{ color: theme.colors.text.white, marginRight: 8 }}
          />
          <View>
            <Text color="white">
              1. Pastikan Nama Peserta Tour Tertera pada dokumen KK
            </Text>
            <Text color="white">
              2. Nama Peserta Tour harus lengkap sesuai dengan masing - masing
              KTP
            </Text>
            <Text color="white">
              3. Dokumen Passport harus valid sampai 27 Desember 2026
            </Text>
          </View>
        </View>
        <Text bold size="m" style={styles.viewMode}>
          {t(['Kelengkapan Dokumen', 'Supporting documents'])}
        </Text>
        <UploadFieldInput
          title="KTP Pemilik Toko"
          fileUrl={owner?.profile?.idCardPhoto || ''}
          disabled
          titleStyle={{ size: 'xs' }}
          required
        />
        <View style={styles.npwpBox}>
          <UploadFieldInput
            title="NPWP"
            fileUrl={ownerNPWP || ''}
            onFileSelected={(newUrl) => {
              onChangeForm({ ownerNPWP: newUrl });
            }}
            disabled={!editable}
            titleStyle={{ size: 'xs' }}
            inputAccept=".jpg"
            required
          />
          <RadioGroup
            options={[
              { label: 'NPWP', value: 'NPWP', 'data-testid': 'NPWP' },
              { label: 'KTP', value: 'KTP', 'data-testid': 'KTP' },
            ]}
            selectedOption={selectedRadio}
            setSelectedOption={(value) => {
              let attachment = selectedRadio;
              if (value === 'NPWP') {
                attachment = AttachmentCardTypes.NPWP;
              } else {
                attachment = AttachmentCardTypes.KTP;
              }
              setFormChanged(true);
              setSelectedRadio(attachment);
            }}
            horizontal
            disabled={!editable}
          />
        </View>
        <UploadFieldInput
          title="KK"
          fileUrl={ownerKK || ''}
          onFileSelected={(newImageUrl) => {
            onChangeForm({ ownerKK: newImageUrl });
          }}
          disabled={!editable}
          titleStyle={{ size: 'xs' }}
          inputAccept="application/pdf"
          required
        />
        <TourParticipantBox
          title="Peserta Tour 1"
          data={{
            identification: firstParticipantIDPhoto || '',
            name: firstParticipantFullName || '',
            passport: firstParticipantPassport || '',
            phoneNumber: firstParticipantPhoneNumber || '',
            passPhoto: firstParticipantPortraitPhoto || '',
          }}
          onChangename={(text) => {
            onChangeForm({ firstParticipantFullName: text });
          }}
          onChangePhoneNumber={(text) => {
            onChangeForm({ firstParticipantPhoneNumber: text });
          }}
          onChangePassport={(newImageUrl) => {
            onChangeForm({ firstParticipantPassport: newImageUrl });
          }}
          onChangeIdentification={(newImageUrl) => {
            onChangeForm({ firstParticipantIDPhoto: newImageUrl });
          }}
          onChangePassPhoto={(newImageUrl) => {
            onChangeForm({ firstParticipantPortraitPhoto: newImageUrl });
          }}
          disabled={!editable}
        />
        {participant2Exist ? (
          <TourParticipantBox
            title="Peserta Tour 2"
            data={{
              identification: secondParticipantIDPhoto || '',
              name: secondParticipantFullName || '',
              passport: secondParticipantPassport || '',
              phoneNumber: secondParticipantPhoneNumber || '',
              passPhoto: secondParticipantPortraitPhoto || '',
            }}
            onChangename={(text) => {
              onChangeForm({ secondParticipantFullName: text });
            }}
            onChangePhoneNumber={(text) => {
              onChangeForm({ secondParticipantPhoneNumber: text });
            }}
            onChangePassport={(newImageUrl) => {
              onChangeForm({ secondParticipantPassport: newImageUrl });
            }}
            onChangeIdentification={(newImageUrl) => {
              onChangeForm({ secondParticipantIDPhoto: newImageUrl });
            }}
            onChangePassPhoto={(newImageUrl) => {
              onChangeForm({ secondParticipantPortraitPhoto: newImageUrl });
            }}
            disabled={!editable}
          />
        ) : null}

        <View>
          <Text style={styles.marignTop16}>
            <Text>Status Verifikasi</Text>
            {currentStatus === StoreProgramStatus.WAITING && (
              <Text bold size="m" style={[styles.lBottomSpacing, styles.red]}>
                *
              </Text>
            )}
          </Text>
          <SegmentedControl
            containerStyle={styles.marignTop16}
            data-testid="btn-status"
            selected={verifStatusOptions.indexOf(
              currentStatus === StoreProgramStatus.WAITING
                ? StoreProgramStatus.UNVERIFIED
                : currentStatus,
            )}
            setSelected={(value) => {
              if (verifStatusOptions[value] === StoreProgramStatus.VERIFIED) {
                setUnverifiedCategories(undefined);
                setCurrentStatus(StoreProgramStatus.VERIFIED);
              } else {
                if (
                  !unverifiedCategories ||
                  unverifiedCategories.length === 0
                ) {
                  setUnverifiedCategories([emptyUnverifiedCategories]);
                }
                setCurrentStatus(StoreProgramStatus.UNVERIFIED);
              }
            }}
            options={verifStatusOptions}
            disabled={viewMode || adminEditMode}
          />
        </View>
        {!viewMode && currentStatus === StoreProgramStatus.UNVERIFIED && (
          <View>
            <View style={[styles.viewMode, styles.unverifContainer]}>
              <Text bold size="m">
                Kategori Unverified
              </Text>
              <Text
                bold
                size="m"
                style={{
                  color: colors.text.red,
                }}
              >
                *
              </Text>
            </View>
            {unverifiedCategories?.map(
              (
                { categoryType, problemDetail, nextAction, categoryDetailEnum },
                idx,
              ) => {
                let isFirstOwnerFilter = [
                  'KTP_PARTICIPANT_1',
                  'DATA_PARTICIPANT_1',
                ];
                let isSecondOwnerFilter = [
                  'KTP_PARTICIPANT_2',
                  'DATA_PARTICIPANT_2',
                ];

                let detailGroup = categoryDetailUnverifPP.filter(
                  (detailCategory) => {
                    let defaultFilter =
                      detailCategory.categoryType === categoryType; //Default filter based on category type chosen
                    let participantAloneFilter = !detailCategory.detailCategoryId // Filter out PARTICIPANT_2 if no second participant
                      .toString()
                      .includes('PARTICIPANT_2');

                    let firstOwnerFilter = !Boolean(
                      // Filter out PARTICIPANT_1 if first participant is owner
                      isFirstOwnerFilter.find((type) =>
                        detailCategory.detailCategoryId
                          .toString()
                          .includes(type),
                      ),
                    );

                    let secondOwnerFilter = !Boolean(
                      // Filter out PARTICIPANT_2 if first participant is owner
                      isSecondOwnerFilter.find((type) =>
                        detailCategory.detailCategoryId
                          .toString()
                          .includes(type),
                      ),
                    );

                    let filter = defaultFilter;

                    if (!participant2Exist) {
                      // add participant alone filter if no second participant
                      filter = filter && participantAloneFilter;
                    }

                    if (isFirstParticipantOwner) {
                      // add first owner filter if first participant is owner
                      filter = filter && firstOwnerFilter;
                    } else if (isSecondParticipantOwner) {
                      // add second owner filter if second participant is owner
                      filter = filter && secondOwnerFilter;
                    }

                    return filter;
                  },
                );

                let problemGroup =
                  // Get problem group based on detail category chosen
                  categoryDetailUnverifPP.find(
                    (detailCategory) =>
                      detailCategory.detailCategoryId === categoryDetailEnum,
                  )?.problems || [];

                let categoryTypes = categoryTypeUnverifPP.filter((
                  type, // Filter out 'Data' type if first participant is owner and no second participant since the detail group will be empty
                ) =>
                  type === 'Data'
                    ? !(isFirstParticipantOwner && !participant2Exist)
                    : true,
                );

                return (
                  <>
                    <View style={styles.unverifReasonBox}>
                      <Dropdown
                        label="Tipe Kategori"
                        value={categoryType}
                        onChange={(value: string) =>
                          onChangeUnverifiedCategories(
                            { categoryType: value },
                            idx,
                            [
                              'categoryDetailEnum',
                              'problemDetail',
                              'nextAction',
                              'categoryDetail',
                            ],
                          )
                        }
                        options={categoryTypes.map((category) => ({
                          label: category,
                          value: category,
                        }))}
                        required
                        containerStyle={[styles.w20, styles.mRightSpacing]}
                      />
                      <Dropdown
                        label="Detail Kategori"
                        value={categoryDetailEnum}
                        onChange={(value: CategoryDetailEnum, label: string) =>
                          onChangeUnverifiedCategories(
                            {
                              categoryDetailEnum: value,
                              categoryDetail: label,
                            },
                            idx,
                            ['problemDetail', 'nextAction'],
                          )
                        }
                        options={detailGroup.map(
                          ({ detailName, detailCategoryId }) => ({
                            label: detailName,
                            value: detailCategoryId,
                          }),
                        )}
                        required
                        containerStyle={[styles.w20, styles.mRightSpacing]}
                      />
                      <Dropdown
                        label="Permasalahan Ditemukan"
                        value={problemDetail}
                        onChange={(_v, _l, data) => {
                          onChangeUnverifiedCategories(
                            {
                              problemDetail: data?.problem || '',
                              nextAction: data?.nextAction,
                            },
                            idx,
                          );
                        }}
                        options={problemGroup.map(
                          ({ problem, nextAction }) => ({
                            label: problem,
                            value: problem,
                            data: { problem, nextAction },
                          }),
                        )}
                        required
                        containerStyle={[styles.w40, styles.mRightSpacing]}
                      />
                      {idx > 0 ? (
                        <Button
                          large
                          preset="secondary"
                          title="-"
                          onPress={() => deleteUnverifiedCategories(idx)}
                          style={styles.w10}
                        />
                      ) : null}
                    </View>
                    {nextAction ? (
                      <Text
                        style={styles.marignTop16}
                      >{`Next Action: ${nextAction}`}</Text>
                    ) : null}
                    <View style={[styles.border, styles.verticalSpacing]} />
                  </>
                );
              },
            )}
            <Text
              color={
                unverifiedCategories && unverifiedCategories.length < 3
                  ? 'blue'
                  : 'grey'
              }
              onPress={() => {
                if (unverifiedCategories && unverifiedCategories.length < 3) {
                  setUnverifiedCategories([
                    ...unverifiedCategories,
                    emptyUnverifiedCategories,
                  ]);
                }
              }}
              style={styles.marignTop16}
            >
              + Tambah Kategori
            </Text>
          </View>
        )}
        {!viewMode && (
          <View style={styles.buttonContainer}>
            <Button
              data-testid="btn-batal-klaim"
              isLoading={unclaimLoading}
              preset="secondary"
              title={t(['Batal Klaim', 'Unclaim'])}
              onPress={() => {
                if (
                  (data.status === StoreProgramStatus.UNVERIFIED &&
                    Boolean(data.isLastReuploadAt)) ||
                  data.status === StoreProgramStatus.WAITING
                ) {
                  unClaimProgramParticipant({
                    variables: {
                      storeId: data.store?.id || '',
                      programId: data.StoreProgram?.id || '',
                      type: ClaimStoreType.UNCLAIM,
                    },
                  });
                } else {
                  refetchData();
                }
              }}
              style={styles.buttonWidth}
            />

            <Button
              style={styles.buttonWidth}
              data-testid="btn-perbarui-data"
              isLoading={updatedLoading}
              disabled={isButtonDisabled()}
              preset="primary"
              title={t(['Perbarui Data', 'Update Data'])}
              onPress={onUpdatePress}
            />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  npwpBox: {
    borderWidth: 1,
    padding: spacing.s,
    borderColor: theme.colors.border.primary,
    marginVertical: spacing.m,
  },
  w10: { width: '10%' },
  w20: { width: '20%' },
  w40: { width: '40%' },
  mRightSpacing: { marginRight: spacing.m },
  viewMode: {
    borderTopColor: colors.button.border.primary,
    borderTopWidth: 1,
    marginTop: spacing.l,
    paddingVertical: spacing.m,
  },
  border: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.button.border.primary,
  },
  verticalSpacing: {
    marginVertical: spacing.m,
  },
  rootContainer: {
    width: '100%',
    minWidth: 0,
    maxWidth: 720,
    backgroundColor: colors.background.white,
    borderWidth: 1,
    borderColor: colors.border.primary,
  },
  formContainer: {
    paddingHorizontal: spacing.m,
    paddingBottom: spacing.m,
    marginTop: spacing.s,
  },
  editModeContainer: {
    position: 'absolute',
    right: spacing.s,
    top: 0,
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    height: 42,
    justifyContent: 'center',
    backgroundColor: colors.background.blue,
    alignItems: 'center',
    paddingHorizontal: spacing.m,
  },
  marignTop16: { marginTop: spacing.m, width: '100%' },
  infoContainer: {
    flexDirection: 'row',
    marginTop: spacing.m,
    padding: spacing.m,
    backgroundColor: theme.colors.background.blue,
    borderRadius: spacing.s,
  },
  unverifContainer: { flexDirection: 'row', marginVertical: 8 },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 32,
  },
  buttonWidth: {
    width: 260,
  },
  unverifReasonBox: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    marginVertical: 6,
  },
  red: { color: colors.text.red },
  lBottomSpacing: {
    marginBottom: spacing.l,
  },
});
